/**
 * Side-column user search for session participants (tutors, students, staff).
 */

import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';
import { getCoordinators, getOrganizers } from '@/services/users.service';
import { getTutors } from '@/services/inperson/tutors.service';
import { getStudents } from '@/services/inperson/students.service';
import { useAuthStore } from '@/stores/authStore';
import type { UserWithProfile, UsersSearchPage } from '@/types/user.types';
import { VOLUNTEER_ROLE_CODES, getUserRoleGroup } from '@/types/user.types';
import type { Role } from '@/types/role.types';
import { facilitatorRoleForSession } from '@/components/sessions/inperson/session-form-utils';
import {
  type OrganizerFilters,
  ORGANIZER_SESSION_GROUPS,
} from '@/components/common/OrganizerFilters';
import { availabilityKeysToDays } from '@/utils/common-utils';
import { useRoles } from '@/hooks/useRoles';
import { roleKind } from '@/utils/role-label-utils';

/** List filters accepted by the side-column users search. */
type UsersSearchListFilters = {
  statuses?: string[];
  subjects?: string[];
  availability?: string[];
  establishmentId?: string;
  roleCodes?: string[];
  levelIds?: string[];
  classIds?: string[];
  specialtyIds?: string[];
  createdFrom?: Date;
  createdTo?: Date;
};

/** Params for {@link useUsersSearch}. */
interface UseUsersSearchParams {
  role?:
    | 'tutor'
    | 'student'
    | 'coordinator'
    | 'volunteer'
    | 'all-organizers'
    | 'tutor_remote'
    | 'student_remote';
  excludedIds?: string[];
  pageSize?: number;
  filters?: UsersSearchListFilters | OrganizerFilters | { establishmentId?: string };
  /** Extra gate on top of `!!role` — e.g. skip fetching until a scoping establishment is chosen. */
  enabled?: boolean;
  /** Filter by presence day(s) on user profiles (0=Sunday .. 6=Saturday). */
  availabilityDays?: number[];
  /**
   * Precise slot filter: when provided alongside availabilityDays, sends
   * availabilityDayOfWeek + availabilityStartTime + availabilityEndTime to the API
   * instead of the day-only filter.  All three must be set together to take effect.
   */
  availabilityStartTime?: string;
  availabilityEndTime?: string;
}

/** Shared pagination / search / exclude params for every users-search fetch. */
interface UsersSearchBaseParams {
  search: string;
  page: number;
  pageSize: number;
  excludeIds: string[];
  availabilityDays?: number[];
}

/** Empty users-search page used before the first fetch. */
function emptyUsersSearchPage(): UsersSearchPage {
  return { data: [], total: 0, hasMore: false };
}

/** Return the array when it has items, otherwise undefined (omit empty filters). */
function nonEmpty<T>(values: T[] | undefined): T[] | undefined {
  if (!values || values.length === 0) return undefined;
  return values;
}

/** In-person tutors and volunteers — remote roles stay out of the tutor picker. */
function isInPersonSessionOrganizerRole(role: Role): boolean {
  const roleGroup = role.roleGroup || '';
  return ORGANIZER_SESSION_GROUPS.has(roleGroup) && role.mode !== 'remote';
}

/** Role codes for the in-person tutor picker, or undefined to leave the API unfiltered. */
function resolveTutorRoleCodes(
  requested: string[] | undefined,
  roles: Role[],
): string[] | undefined {
  if (requested && requested.length > 0) return requested;
  if (!requested) return undefined;
  return roles.filter(isInPersonSessionOrganizerRole).map((role) => role.code);
}

/** Availability days for remote-tutor search: filter days, else the fallback. */
function resolveRemoteTutorAvailabilityDays(
  filterDays: number[],
  fallbackDays: number[] | undefined,
): number[] | undefined {
  if (filterDays.length > 0) return filterDays;
  return fallbackDays;
}

/** Role codes sent when searching students (`student_remote` only for remote). */
function studentSearchRoleCodes(role: 'student' | 'student_remote'): string[] | undefined {
  if (role === 'student_remote') return ['student_remote'];
  return undefined;
}

/** Wrap a paginated API response as a users-search page. */
function toUsersSearchPage(
  page: number,
  pageSize: number,
  response: { data: UserWithProfile[]; total: number },
): UsersSearchPage {
  return {
    data: response.data,
    total: response.total,
    hasMore: page * pageSize < response.total,
  };
}

/** Fetch one page of coordinators for the side-column search. */
async function fetchCoordinatorSearchPage(
  baseParams: UsersSearchBaseParams,
  filters: UseUsersSearchParams['filters'],
): Promise<UsersSearchPage> {
  const coordinatorFilters = filters as { establishmentId?: string } | undefined;
  const response = await getCoordinators({
    ...baseParams,
    establishmentId: coordinatorFilters?.establishmentId,
  });
  return toUsersSearchPage(baseParams.page, baseParams.pageSize, response);
}

/** Fetch one page of volunteers (VSC only) for the side-column search. */
async function fetchVolunteerSearchPage(
  baseParams: UsersSearchBaseParams,
  filters: UseUsersSearchParams['filters'],
): Promise<UsersSearchPage> {
  const volunteerFilters = filters as { establishmentId?: string } | undefined;
  const response = await getOrganizers({
    ...baseParams,
    establishmentId: volunteerFilters?.establishmentId,
    // VSC only — no tutors, no coordinators (unlike `role: 'tutor'`).
    roleGroups: ['volunteers'],
  });
  return toUsersSearchPage(baseParams.page, baseParams.pageSize, response);
}

/** Fetch one page of all organizers for the side-column search. */
async function fetchAllOrganizersSearchPage(
  baseParams: UsersSearchBaseParams,
  filters: UseUsersSearchParams['filters'],
): Promise<UsersSearchPage> {
  const organizerFilters = filters as OrganizerFilters | undefined;
  const response = await getOrganizers({
    ...baseParams,
    statuses: nonEmpty(organizerFilters?.statuses),
    establishmentId: organizerFilters?.establishmentId,
    roleCodes: nonEmpty(organizerFilters?.roleCodes),
  });
  return toUsersSearchPage(baseParams.page, baseParams.pageSize, response);
}

/** Fetch one page of remote tutors for the side-column search. */
async function fetchTutorRemoteSearchPage(
  baseParams: UsersSearchBaseParams,
  filters: UseUsersSearchParams['filters'],
): Promise<UsersSearchPage> {
  const tutorFilters = filters as UsersSearchListFilters | undefined;
  const filterDays = availabilityKeysToDays(tutorFilters?.availability ?? []);
  const response = await getTutors({
    ...baseParams,
    statuses: nonEmpty(tutorFilters?.statuses),
    subjectIds: nonEmpty(tutorFilters?.subjects),
    establishmentId: tutorFilters?.establishmentId,
    availabilityDays: resolveRemoteTutorAvailabilityDays(filterDays, baseParams.availabilityDays),
    roleCodes: ['tutor_remote'],
  });
  return toUsersSearchPage(baseParams.page, baseParams.pageSize, response);
}

/** Fetch one page of in-person tutors and volunteers for the side-column search. */
async function fetchTutorSearchPage(
  baseParams: UsersSearchBaseParams,
  filters: UseUsersSearchParams['filters'],
  roles: Role[],
): Promise<UsersSearchPage> {
  const organizerFilters = filters as OrganizerFilters | undefined;
  const response = await getOrganizers({
    ...baseParams,
    statuses: nonEmpty(organizerFilters?.statuses),
    subjectIds: nonEmpty(organizerFilters?.subjects),
    establishmentId: organizerFilters?.establishmentId,
    roleCodes: resolveTutorRoleCodes(organizerFilters?.roleCodes, roles),
    // Restrict to tutors+volunteers only (no coordinators) via unified endpoint
    roleGroups: ['tutors', 'volunteers'],
  });
  return toUsersSearchPage(baseParams.page, baseParams.pageSize, response);
}

/** Fetch one page of students (in-person or remote) for the side-column search. */
async function fetchStudentSearchPage(
  baseParams: UsersSearchBaseParams,
  filters: UseUsersSearchParams['filters'],
  role: 'student' | 'student_remote',
): Promise<UsersSearchPage> {
  const studentFilters = filters as UsersSearchListFilters | undefined;
  const response = await getStudents({
    ...baseParams,
    statuses: nonEmpty(studentFilters?.statuses),
    subjectIds: nonEmpty(studentFilters?.subjects),
    levelIds: nonEmpty(studentFilters?.levelIds),
    establishmentId: studentFilters?.establishmentId,
    classIds: nonEmpty(studentFilters?.classIds),
    specialtyIds: nonEmpty(studentFilters?.specialtyIds),
    dateFrom: studentFilters?.createdFrom,
    dateTo: studentFilters?.createdTo,
    roleCodes: studentSearchRoleCodes(role),
  });
  return toUsersSearchPage(baseParams.page, baseParams.pageSize, response);
}

/** Dispatch the users-search fetch to the role-specific page loader. */
function fetchUsersSearchPage(
  role: UseUsersSearchParams['role'],
  baseParams: UsersSearchBaseParams,
  filters: UseUsersSearchParams['filters'],
  roles: Role[],
): Promise<UsersSearchPage> {
  switch (role) {
    case 'coordinator':
      return fetchCoordinatorSearchPage(baseParams, filters);
    case 'volunteer':
      return fetchVolunteerSearchPage(baseParams, filters);
    case 'all-organizers':
      return fetchAllOrganizersSearchPage(baseParams, filters);
    case 'tutor_remote':
      return fetchTutorRemoteSearchPage(baseParams, filters);
    case 'tutor':
      return fetchTutorSearchPage(baseParams, filters, roles);
    case 'student':
    case 'student_remote':
      return fetchStudentSearchPage(baseParams, filters, role);
    default:
      return Promise.resolve(emptyUsersSearchPage());
  }
}

/** Side-column typeahead over tutors/students/staff for session participant pickers. */
export const useUsersSearch = ({
  role,
  excludedIds = [],
  pageSize = 5,
  filters,
  enabled = true,
  availabilityDays,
  availabilityStartTime,
  availabilityEndTime,
}: UseUsersSearchParams = {}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const currentUser = useAuthStore((state) => state.user);
  const userRoles = useAuthStore((state) => state.roles);
  const { data: allRoles = [] } = useRoles();

  const isVolunteerRoleCode = React.useCallback(
    (code: string): boolean => {
      const fullRole = allRoles.find((r) => r.code === code);
      if (fullRole) return fullRole.roleGroup === 'volunteers';
      return (VOLUNTEER_ROLE_CODES as readonly string[]).includes(code.toLowerCase());
    },
    [allRoles],
  );

  const isCurrentUserVolunteer = useMemo(() => {
    if (!userRoles || userRoles.length === 0) return false;
    return userRoles.some((ur) => isVolunteerRoleCode(ur.code));
  }, [userRoles, isVolunteerRoleCode]);

  const isCurrentUserCoordinator = useMemo(() => {
    if (!userRoles || userRoles.length === 0) return false;
    return userRoles.some((ur) => {
      const fullRole = allRoles.find((r) => r.code === ur.code);
      if (fullRole) return fullRole.roleGroup === 'admins' && roleKind(ur.code) === 'coordinator';
      return roleKind(ur.code) === 'coordinator';
    });
  }, [userRoles, allRoles]);

  const isCurrentUserOrganizer = useMemo(() => {
    if (!userRoles || userRoles.length === 0) return false;
    return userRoles.some((ur) => {
      const fullRole = allRoles.find((r) => r.code === ur.code);
      const rg = fullRole?.roleGroup || '';
      return rg === 'tutors' || rg === 'volunteers' || roleKind(ur.code) === 'coordinator';
    });
  }, [userRoles, allRoles]);

  const excludedIdsKey = useMemo(
    () =>
      excludedIds.length > 0 ? [...excludedIds].sort((a, b) => a.localeCompare(b)).join(',') : '',
    [excludedIds],
  );

  const shouldPinSelf = useMemo(() => {
    if (!currentUser) return false;
    if (excludedIds.includes(currentUser.id)) return false;
    return (
      (role === 'tutor' && isCurrentUserVolunteer) ||
      (role === 'volunteer' && isCurrentUserVolunteer) ||
      (role === 'coordinator' && isCurrentUserCoordinator) ||
      (role === 'all-organizers' && isCurrentUserOrganizer)
    );
  }, [
    role,
    isCurrentUserVolunteer,
    isCurrentUserCoordinator,
    isCurrentUserOrganizer,
    currentUser,
    excludedIds,
  ]);

  const adjustedPageSize = useMemo(() => {
    return shouldPinSelf ? pageSize - 1 : pageSize;
  }, [shouldPinSelf, pageSize]);

  const availabilityDaysKey = useMemo(
    () =>
      availabilityDays && availabilityDays.length > 0
        ? [...availabilityDays].sort((a, b) => a - b).join(',')
        : '',
    [availabilityDays],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'users-search',
      role,
      searchQuery,
      page,
      adjustedPageSize,
      excludedIdsKey,
      filters,
      availabilityDaysKey,
    ],
    queryFn: () => {
      const baseParams: UsersSearchBaseParams = {
        search: searchQuery,
        page,
        pageSize: adjustedPageSize,
        excludeIds: excludedIds,
        availabilityDays: nonEmpty(availabilityDays),
      };
      return fetchUsersSearchPage(role, baseParams, filters, allRoles);
    },
    enabled: !!role && enabled,
  });

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (data?.hasMore) {
      setPage((p) => p + 1);
    }
  }, [data?.hasMore]);

  const handleNextPage = useCallback(() => {
    if (data?.hasMore) {
      setPage((p) => p + 1);
    }
  }, [data?.hasMore]);

  const handlePrevPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const usersWithRole: UserWithProfile[] = useMemo(() => {
    const resolveRole = (user: UserWithProfile): UserWithProfile['currentRole'] => {
      if (roleKind(role) === 'tutor') return facilitatorRoleForSession(user);
      if (role === 'volunteer') return 'volunteer';
      if (role === 'coordinator') return 'coordinator';
      if (role === 'all-organizers') {
        const rg = getUserRoleGroup(user);
        if (rg === 'volunteers') return 'volunteer';
        if (rg === 'admins') return 'coordinator';
        return 'tutor';
      }
      return 'student';
    };
    return (data?.data || []).map((user) => ({ ...user, currentRole: resolveRole(user) }));
  }, [data?.data, role]);

  const enrichedCurrentUserRoles = useMemo(
    () =>
      userRoles.map((r) => {
        const fullRole = allRoles.find((fr) => fr.code === r.code);
        return {
          roleId: r.id,
          role: { id: r.id, code: r.code, label: r.label, roleGroup: fullRole?.roleGroup },
        };
      }),
    [userRoles, allRoles],
  );

  const users = useMemo(() => {
    if (!shouldPinSelf || !currentUser) {
      return usersWithRole;
    }

    const selfRoleFromCurrentUser = (): UserWithProfile['currentRole'] => {
      if (role === 'coordinator') return 'coordinator';
      if (role === 'all-organizers') {
        if (isCurrentUserCoordinator) return 'coordinator';
        if (isCurrentUserVolunteer) return 'volunteer';
        return 'tutor';
      }
      return 'volunteer';
    };
    const selfRole: UserWithProfile['currentRole'] = selfRoleFromCurrentUser();

    const currentUserInList = usersWithRole.find((u) => u.id === currentUser.id);
    if (currentUserInList) {
      const rest = usersWithRole.filter((u) => u.id !== currentUser.id);
      return [
        { ...currentUserInList, userRoles: enrichedCurrentUserRoles, isPinned: true },
        ...rest,
      ];
    }

    const pinnedUser: UserWithProfile = {
      id: currentUser.id,
      companyId: currentUser.companyId,
      firebaseUid: currentUser.firebaseUid,
      email: currentUser.email,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      displayName: currentUser.displayName,
      photoUrl: currentUser.photoUrl,
      phone: currentUser.phone,
      status: currentUser.status,
      userRoles: enrichedCurrentUserRoles,
      currentRole: selfRole,
      isPinned: true,
      createdAt: currentUser.createdAt || new Date(),
      updatedAt: currentUser.updatedAt || new Date(),
    };
    return [pinnedUser, ...usersWithRole];
  }, [shouldPinSelf, role, currentUser, enrichedCurrentUserRoles, usersWithRole]);

  return {
    users,
    isLoading,
    error,
    hasMore: data?.hasMore || false,
    total: data?.total || 0,
    page,
    onSearch: handleSearch,
    onLoadMore: handleLoadMore,
    onNextPage: handleNextPage,
    onPrevPage: handlePrevPage,
    searchQuery,
  };
};
